import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal } from "@/components/Motion";

const GAP_IMG =
  "https://images.pexels.com/photos/5094681/pexels-photo-5094681.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const TheGap = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section
      id="gap"
      ref={ref}
      data-testid="gap-section"
      className="relative py-24 md:py-32 px-6 md:px-10 max-w-[1400px] mx-auto"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <div className="lg:col-span-6 order-2 lg:order-1">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-[#C85A3C] mb-6">
              How the Grant Helps
            </p>
            <h2 className="font-serif-display font-light leading-[1.0] tracking-tight text-4xl md:text-6xl text-[#1A1A1A]">
              Bridging the gap during a critical transition.
            </h2>
          </Reveal>

          <Reveal delay={0.1} className="mt-8 space-y-6 text-base md:text-lg leading-relaxed text-[#1A1A1A]/75 max-w-xl">
            <p>
              Mothers apply for services through the New Jersey Division of Developmental
              Disabilities <span className="text-[#1A1A1A] font-medium">(DDD)</span>, while
              children apply for developmental disability services through{" "}
              <span className="text-[#1A1A1A] font-medium">PerformCare</span>.
            </p>
            <p>
              Because the application and eligibility process can be lengthy, grant funding
              offsets the ongoing costs of care, housing, and essential family supports —
              filling the financial gap during this critical transition period.
            </p>
          </Reveal>

          <Reveal delay={0.2} className="mt-10 grid grid-cols-2 gap-px bg-[#1A1A1A]/10 border border-[#1A1A1A]/10">
            {[
              { k: "DDD", v: "Developmental disability services for mothers" },
              { k: "PerformCare", v: "Developmental services for children" },
            ].map((item) => (
              <div key={item.k} className="bg-[#F4F1EA] p-6" data-testid={`gap-tag-${item.k.toLowerCase()}`}>
                <p className="font-serif-display text-2xl text-[#C85A3C] mb-2">{item.k}</p>
                <p className="text-sm text-[#1A1A1A]/70 leading-snug">{item.v}</p>
              </div>
            ))}
          </Reveal>
        </div>

        <div className="lg:col-span-6 order-1 lg:order-2">
          <div className="relative overflow-hidden rounded-2xl rounded-tr-[120px] aspect-[4/3]">
            <motion.img
              src={GAP_IMG}
              alt="A mother laughing and playing with her daughter"
              style={{ y }}
              className="absolute inset-0 w-full h-[118%] object-cover"
              data-testid="gap-image"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default TheGap;
